import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import EventLog from 'src/components/ui/EventLog';
import EventLogFilterPills from 'src/components/ui/EventLogFilterPills';
import ActionButton from 'src/components/ui/ActionButton';

import { clearGameEvents, selectEventGroupFilters, selectGameEvents } from 'src/slices/gameEvents.slice';

import { SCREEN_SIZES } from 'src/vars';

const EventLogModuleWrapper = styled.div`
    display: flex;
    container-type: inline-size;
    flex-direction: column;
    overflow-y: auto;
    height: calc(100% - 56px);
    background: var(--surface);
    border-radius: 8px;
`;

const EventLogModuleHeader = styled.div`
    display: none;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--surface);
    border-bottom: 2px solid var(--border-subtle);

    @media (min-width: ${SCREEN_SIZES.LARGE}px) {
        display: flex;
    }
`;

const EventLogModuleTitle = styled.h2`
    margin: 0;
    padding: 13px 0 12px;
    font-size: 21px;
`;

const EventLogModuleEvents = styled.ul`
    flex: 1;
    padding: 18px 8px;
    margin: 0;

    @media (min-width: ${SCREEN_SIZES.LARGE}px) {
        overflow-y: auto;
    }
`;

const EventLogModuleEvent = styled.li`
    list-style: none;
    margin: 0 0 8px;

    &:last-of-type {
        margin: 0;
    }
`;

const EventLogModuleActionsMenu = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
    padding: 8px 16px;
    border-bottom: 2px solid var(--border-subtle);
`;

// Define event groups for filtering
const GAME_EVENT_GROUPS = {
    sdk: ['pokiTrackingSdkStatusInitialized'],
    gameplay: ['pokiTrackingScreenGameLoadingFinished', 'pokiTrackingScreenGameplayStart', 'pokiTrackingScreenGameplayStop'],
    ads: ['pokiTrackingScreenRewardedBreak', 'pokiTrackingScreenCommercialBreak', 'pokiTrackingScreenDisplayAdRequest', 'pokiTrackingScreenDisplayAdDestroy'],
    measure: ['pokiTrackingMeasure'],
    leaderboard: ['pokiMessageSendScore', 'pokiMessageShowLeaderboard'],
};

export default function EventLogModule() {
    const dispatch = useDispatch();

    const lastEventRef = useRef(null);

    const [isHovered, setIsHovered] = useState(false);

    const gameEvents = useSelector(selectGameEvents);
    const eventGroupFilters = useSelector(selectEventGroupFilters);

    // filter pokiTrackingScreenRewardedBreak events with the same adBreakId, keep only the first one
    const filteredRewardedBreakEvents = useMemo(() => {
        const seenAdBreakIds = new Set();

        return gameEvents.filter(event => {
            if (event.event === 'pokiTrackingScreenRewardedBreak') {
                const adBreakId = event.data?.adBreakId;

                if (seenAdBreakIds.has(adBreakId)) {
                    return false;
                } else {
                    seenAdBreakIds.add(adBreakId);
                    return true;
                }
            }
            return true;
        });
    }, [gameEvents]);

    const filteredGameEvents = useMemo(() => {
        return filteredRewardedBreakEvents.filter(event => {
            for (const [group, isEnabled] of Object.entries(eventGroupFilters)) {
                if (isEnabled && GAME_EVENT_GROUPS[group].includes(event.event)) {
                    return true;
                }
            }
            return false;
        })
    }, [filteredRewardedBreakEvents, eventGroupFilters]);

    const handleClearEventLog = () => {
        dispatch(clearGameEvents());
    };

    useEffect(() => {
        // scroll to bottom of event log when new event is added
        // and user is not hovering over the event log
        if (!isHovered && lastEventRef.current) {
            lastEventRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isHovered, gameEvents]);

    return (
        <EventLogModuleWrapper>
            <EventLogModuleHeader>
                <EventLogModuleTitle>Event Log</EventLogModuleTitle>
                <ActionButton onClickCallback={handleClearEventLog}>Clear</ActionButton>
            </EventLogModuleHeader>
            <EventLogModuleActionsMenu>
                <EventLogFilterPills />
            </EventLogModuleActionsMenu>
            {filteredGameEvents.length > 0 && (
                <EventLogModuleEvents
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {filteredGameEvents.map((gameEvent) => {
                        const isLastEntry = gameEvents[gameEvents.length - 1] === gameEvent;
                        const ref = isLastEntry ? lastEventRef : null;
                        return (
                            <EventLogModuleEvent key={gameEvent.id} ref={ref}>
                                <EventLog
                                    timestamp={gameEvent.timestamp}
                                    eventName={gameEvent.event}
                                    eventData={gameEvent?.data}
                                />
                            </EventLogModuleEvent>
                        );
                    })}
                </EventLogModuleEvents>
            )}
        </EventLogModuleWrapper>
    );
}
